// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
// Remplacer l'import api :
import { getAdminAnnotationsPaginated, adminDeleteAnnotation, updateAnnotationStatus, exportAnnotations, telechargerRapport } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import SkeletonRows from '../components/SkeletonRows';
import ConfirmModal from '../components/ConfirmModal';

const getAnnotationTypes = (t) => ({
  parsing: t('ann_type_parsing'),
  display: t('ann_type_display'),
  error:   t('ann_type_error'),
  anomaly: t('ann_type_anomaly'),
  other:   t('ann_type_other'),
});
const getAnnotationTargets = (t) => ({
  json_pass1: t('ann_target_json1'),
  json_final: t('ann_target_json2'),
  html:       t('ann_target_html'),
  pdf:        t('ann_target_pdf'),
  general:    t('ann_target_general'),
});
const TYPE_COLORS = {
  parsing: { bg: '#fef3c7', text: '#92400e' },
  display: { bg: '#ede9fe', text: '#5b21b6' },
  error:   { bg: '#fee2e2', text: '#991b1b' },
  anomaly: { bg: '#fce7f3', text: '#9d174d' },
  other:   { bg: '#f3f4f6', text: '#374151' },
};
const ANN_STATUS_COLORS = {
  open:       { bg: '#eff6ff', text: '#1d4ed8' },
  processing: { bg: '#fef3c7', text: '#92400e' },
  rejected:   { bg: '#fee2e2', text: '#991b1b' },
  closed:     { bg: '#f0fdf4', text: '#166534' },
};
const RES_STATUS_COLORS = {
  resolved:   { bg: '#f0fdf4', text: '#166534' },
  unresolved: { bg: '#fee2e2', text: '#991b1b' },
  in_progress:{ bg: '#fef3c7', text: '#92400e' },
  unknown:    { bg: '#f3f4f6', text: '#374151' },
};

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const { t, lang } = useI18n();
  const ref = useRef(null);
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}</label>
      <button type="button" onClick={() => setOpen(!open)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-surface)', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: selected.length ? 'var(--black)' : 'var(--text-muted)' }}>
        {selected.length === 0 ? t('filter_all') : selected.map(v => options[v] || v).join(', ')}
        <span style={{ float: 'right' }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, background: 'var(--bg-surface)', border: '1px solid var(--gray)', borderRadius: 6, boxShadow: 'var(--shadow-md)', minWidth: '100%', marginTop: 2 }}>
          {Object.entries(options).map(([k, v]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, background: selected.includes(k) ? 'rgba(59,130,246,0.08)' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(k)} onChange={() => toggle(k)} style={{ accentColor: 'var(--blue)' }} />
              {v}
            </label>
          ))}
          {selected.length > 0 && (
            <div onClick={() => { onChange([]); setOpen(false); }} style={{ padding: '6px 12px', fontSize: 12, color: '#ef4444', cursor: 'pointer', borderTop: '1px solid var(--gray)' }}>
              {t('reset_label')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusPanel = ({ ann, onClose, onSaved, t }) => {
  const [annStatus, setAnnStatus] = useState(ann.annotation_status || 'open');
  const [resStatus, setResStatus] = useState(ann.resolution_status || 'unknown');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const save = async () => {
    setSaving(true); setError('');
    try {
      await updateAnnotationStatus(ann.id, { annotation_status: annStatus, resolution_status: resStatus });
      onSaved(ann.id, annStatus, resStatus);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const ANN_STATUSES   = ['open', 'processing', 'rejected', 'closed'];
  const RES_STATUSES   = ['resolved', 'unresolved', 'in_progress', 'unknown'];
  const annLabels      = { open: t('ann_status_open'), processing: t('ann_status_processing'), rejected: t('ann_status_rejected'), closed: t('ann_status_closed') };
  const resLabels      = { resolved: t('resolution_resolved'), unresolved: t('resolution_unresolved'), in_progress: t('resolution_in_progress'), unknown: t('resolution_unknown') };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 340, background: 'var(--bg-surface)', borderLeft: '1px solid var(--gray)', boxShadow: '-4px 0 20px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column', padding: 24, gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--black)' }}>📌 Statut annotation</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'rgba(128,128,128,0.06)', borderRadius: 8, padding: '8px 12px' }}>
        {ann.content?.substring(0, 100)}{ann.content?.length > 100 ? '...' : ''}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('ann_status_label')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ANN_STATUSES.map(s => {
            const c = ANN_STATUS_COLORS[s];
            return (
              <div key={s} onClick={() => setAnnStatus(s)} style={{ padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${annStatus === s ? 'var(--blue)' : 'var(--gray)'}`, background: annStatus === s ? 'rgba(59,130,246,0.08)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 99, background: c.bg, color: c.text }}>{annLabels[s]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('resolution_label')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {RES_STATUSES.map(s => {
            const c = RES_STATUS_COLORS[s];
            return (
              <div key={s} onClick={() => setResStatus(s)} style={{ padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${resStatus === s ? 'var(--blue)' : 'var(--gray)'}`, background: resStatus === s ? 'rgba(59,130,246,0.08)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 99, background: c.bg, color: c.text }}>{resLabels[s]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {error && <p style={{ color: '#ef4444', fontSize: 12 }}>❌ {error}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', fontSize: 13, cursor: 'pointer', color: 'var(--black)' }}>{t('panel_close')}</button>
        <button onClick={save} disabled={saving} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳...' : t('panel_save')}
        </button>
      </div>
    </div>
  );
};

const AdminAnnotations = () => {
  const [annotations, setAnnotations]     = useState([]);
  const [loading, setLoading]             = useState(false);
  const [total, setTotal]                 = useState(0);
  const [filterTypes, setFilterTypes]     = useState([]);
  const [filterAids, setFilterAids]       = useState([]);
  const [filterUids, setFilterUids]       = useState([]);
  const [filterAnnStatus, setFilterAnnStatus] = useState([]);
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [analysisOptions, setAnalysisOptions] = useState({});
  const [userOptions, setUserOptions]         = useState({});
  const [panelAnn, setPanelAnn]           = useState(null);
  const [offset, setOffset]     = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const ANNOTATION_TYPES   = getAnnotationTypes(t);
  const ANNOTATION_TARGETS = getAnnotationTargets(t);
  const isAdmin = ['admin', 'dev'].includes(user?.role);
  const [confirmState, setConfirmState] = useState(null); // { message, onConfirm }
  const loadAll = useCallback(async (filters = {}, off = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const data = await getAdminAnnotationsPaginated(filters, off, PAGE_SIZE);
      const anns = data.annotations || [];
      setAnnotations(prev => append ? [...prev, ...anns] : anns);
      setTotal(data.total || anns.length);
      setHasMore(data.has_more || false);
      setOffset(off + PAGE_SIZE);
      if (!append) {
        const aOpts = {}, uOpts = {};
        anns.forEach(a => {
          if (a.analysis_id) aOpts[a.analysis_id] = a.filename || a.analysis_id.substring(0, 8) + '...';
          if (a.user_id)     uOpts[a.user_id]     = a.user_email || a.user_id.substring(0, 8) + '...';
        });
        setAnalysisOptions(aOpts);
        setUserOptions(uOpts);
      }
    } catch (e) { console.error('❌', e); }
    finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const currentFilters = () => ({ types: filterTypes, analysis_ids: filterAids, user_ids: filterUids, date_from: dateFrom, date_to: dateTo, ann_statuses: filterAnnStatus });
  const handleSearch = () => { setOffset(0); loadAll(currentFilters(), 0, false); };
  const handleReset  = () => {
    setFilterTypes([]); setFilterAids([]); setFilterUids([]);
    setDateFrom(''); setDateTo(''); setFilterAnnStatus([]);
    setOffset(0);
    loadAll({}, 0, false);
  };
  const handleLoadMore = () => loadAll(currentFilters(), offset, true);
  const handleDelete = (id) => {
    setConfirmState({
      message: t('admin_delete_confirm'),
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await adminDeleteAnnotation(id);
          setAnnotations(prev => prev.filter(a => a.id !== id));
          setTotal(prev => prev - 1);
        } catch (e) { alert(t('err_prefix') + e.message); }
      }
    });
  };

  const handleStatusSaved = (id, annStatus, resStatus) => {
    setAnnotations(prev => prev.map(a => a.id === id ? { ...a, annotation_status: annStatus, resolution_status: resStatus } : a));
  };

  const formatDate = (d) =>
    new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  const activeFiltersCount =
      filterTypes.length + filterAids.length + filterUids.length +
      filterAnnStatus.length + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {panelAnn && <StatusPanel ann={panelAnn} onClose={() => setPanelAnn(null)} onSaved={handleStatusSaved} t={t} />}

      <main className="container mx-auto px-4 py-8 max-w-7xl flex-1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 2 }}>📌 {t('admin_annotations_title')}</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {total} annotation{total > 1 ? 's' : ''}
              {activeFiltersCount > 0 && ` — ${activeFiltersCount} filtre${activeFiltersCount > 1 ? 's' : ''} actif${activeFiltersCount > 1 ? 's' : ''}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isAdmin && <Button onClick={() => exportAnnotations().catch(e => alert(e.message))} variant="outline" size="sm">{t('export_excel')}</Button>}
            <Button onClick={handleReset} variant="outline" size="sm">{t('btn_refresh')}</Button>
          </div>
        </div>

        <div style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', padding: '16px 20px', marginBottom: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <MultiSelect label="Type"         options={ANNOTATION_TYPES}  selected={filterTypes} onChange={setFilterTypes} />
          <MultiSelect label="Analyse"      options={analysisOptions}   selected={filterAids}  onChange={setFilterAids} />
          <MultiSelect label="Utilisateur"  options={userOptions}       selected={filterUids}  onChange={setFilterUids} />
          <MultiSelect
            label={t('ann_status_label')}
            options={{ open: t('ann_status_open'), processing: t('ann_status_processing'), rejected: t('ann_status_rejected'), closed: t('ann_status_closed') }}
            selected={filterAnnStatus}
            onChange={setFilterAnnStatus}
          />
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('filter_date_from')}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('filter_date_to')}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }} />
          </div>
          <Button onClick={handleSearch} style={{ alignSelf: 'end' }}>{t('filter_label')}</Button>
        </div>

        {loading ? (
          <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody><SkeletonRows cols={10} rows={5} /></tbody>
            </table>
          </div>        
        ) : annotations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t('no_annotations')}</div>
        ) : (
          <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                  {[t('col_type'), t('col_target'), t('col_analysis'), t('col_user'), t('col_content'), t('ann_status_label'), t('resolution_label'), t('col_date'), t('col_src'), t('col_actions')].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {annotations.map((ann, i) => {
                  const tc  = TYPE_COLORS[ann.type] || TYPE_COLORS.other;
                  const ac  = ANN_STATUS_COLORS[ann.annotation_status || 'open'];
                  const rc  = RES_STATUS_COLORS[ann.resolution_status || 'unknown'];
                  const annLabels = { open: t('ann_status_open'), processing: t('ann_status_processing'), rejected: t('ann_status_rejected'), closed: t('ann_status_closed') };
                  const resLabels = { resolved: t('resolution_resolved'), unresolved: t('resolution_unresolved'), in_progress: t('resolution_in_progress'), unknown: t('resolution_unknown') };
                  return (
                    <tr key={ann.id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: tc.bg, color: tc.text, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap' }}>{ANNOTATION_TYPES[ann.type] || ann.type}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{ANNOTATION_TARGETS[ann.target] || ann.target}</td>
                      <td style={{ padding: '10px 14px', maxWidth: 160 }}>
                        <button type="button" onClick={() => telechargerRapport(ann.analysis_id).catch(e => alert(e.message))}
                           style={{ color: 'var(--blue)', textDecoration: 'none', fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', maxWidth: '100%' }}
                           onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                           onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}>
                          {ann.filename || ann.analysis_id?.substring(0, 8) + '...'}
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{ann.user_email || ann.user_id?.substring(0, 8) + '...'}</td>
                      <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                        <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden', maxHeight: 60, color: 'var(--black)' }}>{ann.content}</p>
                        {ann.updated_at && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('modified_label')}</span>}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: ac.bg, color: ac.text }}>{annLabels[ann.annotation_status || 'open']}</span>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: rc.bg, color: rc.text }}>{resLabels[ann.resolution_status || 'unknown']}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(ann.created_at)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {ann.ia_generated === 1
                          ? <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, padding: '2px 6px', borderRadius: 99 }}>{t('src_ia')}</span>
                          : <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11, padding: '2px 6px', borderRadius: 99 }}>{t('src_human')}</span>}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {isAdmin && (
                            <button onClick={() => setPanelAnn(ann)} style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', color: 'var(--blue)', fontSize: 11, cursor: 'pointer' }}>
                              {t('ann_status_label')}
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => handleDelete(ann.id)} style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                              {t('delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={handleLoadMore} variant="outline" size="sm" disabled={loadingMore}>
              {loadingMore ? t('loading_more') : t('load_more')}
            </Button>
          </div>
        )}
        {confirmState && (
          <ConfirmModal
            message={confirmState.message}
            onConfirm={confirmState.onConfirm}
            onCancel={() => setConfirmState(null)}
          />
        )}
      </main>
    </div>
  );
};

export default AdminAnnotations;